'use client';

import { ReactElement, useState } from 'react';
import { useLocale } from '@hooks/useLocale';
import { useTranslationWithSelfFallback } from '@i18n/useTranslationWithSelfFallback';
import Image from 'next/image';
import cn from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';
import SearchBar from '@components/algolia/SearchBar';
import CartBadge from './CartBadge';
import MenuFlyout from '@components/global/UtilityNav/MenuFlyout';
import { useCustomer } from '@adapters/bigcommerce/hooks/customer/index';
import { useEscapeKey } from '@hooks/useEscapeKey';
import useBreakpoint, { Breakpoints } from '@hooks/useBreakpoint';
import AccountMenu from '_components/AccountMenu';
import IconButton from '_components/UI/IconButton';
import TextButton from '_components/UI/TextButton';
import LoggedInContent from '_components/AccountMenu/LoggedInContent';
import {
  ShoppingCartIcon,
  CaretDownIcon,
  UserCircleIcon,
  ListIcon,
  XIcon,
} from '@phosphor-icons/react';
import Modal from '@components/global/Modal';
import LoggedOutContent from '_components/AccountMenu/LoggedOutContent';
import {
  AccountMenuLevels,
  trackAccountMenuEvents,
} from '_utils/GA4/trackAccountMenuEvents';
import {
  MegaMenuLevels,
  trackMegaMenuNavigation,
} from '_utils/GA4/trackMegaMenuEvents';

import styles from './PrimaryNavBar.module.scss';

export interface PrimaryNavBarProps {
  className?: string;
  isMobileNavOpen?: boolean;
  onMobileNavToggle: () => void;
}

const PrimaryNavBar = ({
  className,
  isMobileNavOpen = false,
  onMobileNavToggle,
}: PrimaryNavBarProps): ReactElement => {
  const locale = useLocale();
  const t = useTranslationWithSelfFallback('common');
  const { customer, customerIsLoggedIn } = useCustomer();
  const isMobile = useBreakpoint(Breakpoints.sm);
  const [showAccountPopover, setShowAccountPopover] = useState(false);
  const [referenceElement, setReferenceElement] = useState<Element | null>(
    null
  );

  const toggleAccountPopover = () => {
    trackAccountMenuEvents({
      data: {
        level: AccountMenuLevels.level_1,
        linkUrl: null,
        linkText: null,
      },
    });

    setShowAccountPopover(!showAccountPopover);
  };

  useEscapeKey(showAccountPopover, () => {
    trackAccountMenuEvents({
      data: {
        level: AccountMenuLevels.level_1,
        linkUrl: null,
        linkText: customerIsLoggedIn
          ? `${t('nav.utility.account.greeting.loggedIn')} ${
              customer?.firstName
            } ${t('nav.utility.account.greeting.welcomeBack')}`
          : null,
      },
    });
    setShowAccountPopover(false);
  });

  const handleMobileMenuClick = () => {
    if (!isMobileNavOpen) {
      trackMegaMenuNavigation({
        level: MegaMenuLevels.level_0,
        linkUrl: null,
        linkText: '',
        menuPath: '',
      });
    }

    onMobileNavToggle();
  };

  return (
    <div className={cn(styles.primaryNavBar, className)}>
      <div className={styles.container}>
        <div className={styles.logoSection}>
          <a
            href={`/${locale}`}
            className={styles.logo}
            aria-label={t('nav.logo.ariaLabel')}
          >
            <span className={styles.logoIcon} aria-hidden="true">
              <Image
                src="/images/mec-logo-desktop.svg"
                alt="mec logo"
                quality={100}
                priority
                className={styles.logoImageDesktop}
                fill
                sizes="100vw"
                style={{ objectFit: 'contain' }}
              />
              <Image
                src="/images/mec-logo-mobile.svg"
                alt="mec logo"
                quality={100}
                priority
                className={styles.logoImageMobile}
                fill
                sizes="100vw"
                style={{ objectFit: 'contain' }}
              />
            </span>
          </a>
        </div>
        <div className={styles.searchAndActionsSection}>
          <div className={styles.searchSection}>
            <SearchBar newSearchBarPlaceholder />
          </div>

          <div className={styles.actionsSection}>
            <IconButton
              icon={ShoppingCartIcon}
              type="secondary"
              aria-label={t('nav.utility.cart')}
              shape="square"
              iconWeight="light"
              href={`/${locale}/cart`}
              size="sm"
              iconSize={24}
              className={styles.iconButton}
            >
              <span className={styles.cartBadge}>
                <CartBadge />
              </span>
            </IconButton>

            <div className={styles.accountSection} ref={setReferenceElement}>
              {!customerIsLoggedIn ? (
                <IconButton
                  icon={UserCircleIcon}
                  type="secondary"
                  aria-label={t('nav.utility.account')}
                  shape="square"
                  size="sm"
                  iconSize={24}
                  iconWeight="light"
                  className={styles.iconButton}
                  onClick={toggleAccountPopover}
                />
              ) : (
                <TextButton
                  type="white"
                  size="small"
                  leadingIcon={UserCircleIcon}
                  leadingIconWeight="light"
                  trailingIcon={CaretDownIcon}
                  trailingIconWeight="light"
                  leadingIconSize={20}
                  trailingIconSize={16}
                  className={styles.accountButton}
                  label={
                    <div className={styles.greeting}>
                      {customerIsLoggedIn && customer?.firstName && (
                        <>
                          <div className={styles.greetingLine1}>
                            {t('nav.utility.account.greeting.loggedIn')}{' '}
                            {customer.firstName}
                          </div>
                          <div className={styles.greetingLine2}>
                            {t('nav.utility.account.greeting.welcomeBack')}
                          </div>
                        </>
                      )}
                    </div>
                  }
                  onClick={toggleAccountPopover}
                  aria-label={t('nav.utility.account')}
                  aria-haspopup="true"
                />
              )}

              {showAccountPopover && !isMobile && (
                <OutsideClickHandler
                  onOutsideClick={() => {
                    setShowAccountPopover(false);
                  }}
                  disabled={isMobile}
                >
                  <MenuFlyout
                    showPopover={showAccountPopover}
                    referenceElement={referenceElement}
                    type="account"
                    showArrow={false}
                  >
                    <AccountMenu onClose={() => setShowAccountPopover(false)} />
                  </MenuFlyout>
                </OutsideClickHandler>
              )}
              {isMobile && (
                <Modal
                  closeButtonVisible={false}
                  labelledBy="account-modal-content"
                  isOpen={showAccountPopover}
                  onClose={() => setShowAccountPopover(false)}
                  fitContent
                  bottomPositioning
                  classNames={styles.accountModal}
                >
                  <div id="account-modal-content">
                    {!customerIsLoggedIn ? (
                      <LoggedOutContent
                        onClose={() => setShowAccountPopover(false)}
                      />
                    ) : (
                      <LoggedInContent
                        onClose={() => setShowAccountPopover(false)}
                      />
                    )}
                  </div>
                </Modal>
              )}
            </div>
            <IconButton
              icon={isMobileNavOpen ? XIcon : ListIcon}
              type="secondary"
              aria-label={
                isMobileNavOpen
                  ? t('nav.mobile.header.close.aria')
                  : t('nav.utility.menu')
              }
              className={styles.menuButton}
              shape="square"
              size="sm"
              iconWeight="light"
              iconSize={24}
              onClick={handleMobileMenuClick}
            />
          </div>
        </div>
      </div>
      <div className={styles.separator} />
      <div className={styles.mobileSearchSection}>
        <SearchBar newSearchBarPlaceholder />
      </div>
    </div>
  );
};

export default PrimaryNavBar;
